import React, { useEffect, useState, useMemo } from "react";
import { db } from "../../config/firebase-config.js";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  updateDoc
} from "firebase/firestore";
import { QRCodeCanvas } from "qrcode.react";
import EarningsDashboard from "./EarningsDashboard.jsx";
import MusicianOnboarding from "../../onboarding/MusicianOnboarding.jsx";
import VerificationStatusSection from "../../components/common/VerificationStatusSection.jsx";
import { auth } from "../../config/firebase-config.js";
// Import star logo
import starLogo from "../../assets/requestasong-symbol.png";

// Import the gamification component
import MusicianGamification from "../../components/gamification/MusicianGamification.jsx";

// Import the print ordering component
import PrintOrderInterface from "../../components/printing/PrintOrderInterface.jsx";

// Import referral components
import ReferralDashboard from "../../components/referral/ReferralDashboard.jsx";
import ReferralStatus from "../../components/referral/ReferralStatus.jsx";

// Import debug component (temporary)
// Debug component removed for production

// Import bulk upload component
import BulkSongUpload from "../../components/songs/BulkSongUpload.jsx";

// Import dashboard tour for first-time users
import MusicianDashboardTour from "../../components/tours/MusicianDashboardTour.jsx";

export default function MusicianDashboard({ user, userProfile, verificationStatus: propVerificationStatus, onSignOut }) {
  const [activeTab, setActiveTab] = useState("earnings");
  const [songs, setSongs] = useState([]);
  const [newSongName, setNewSongName] = useState("");
  const [newSongArtist, setNewSongArtist] = useState("");
  const [newSongPrice, setNewSongPrice] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState(""); // NEW
  const [realName, setRealName] = useState(""); // NEW
  const [originalDisplayName, setOriginalDisplayName] = useState("");
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState("");
  const [originalEmail, setOriginalEmail] = useState(""); // NEW
  const [originalRealName, setOriginalRealName] = useState(""); // NEW
  const [editingSongId, setEditingSongId] = useState(null);
  const [editingSongData, setEditingSongData] = useState({ name: "", artist: "", price: "" });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(propVerificationStatus || null);
  
  // 🆕 NEW: Request toggle state
  const [acceptingRequests, setAcceptingRequests] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  
  // Onboarding state
  const [onboardingCompleted, setOnboardingCompleted] = useState(null); // null = loading, true/false = status
  const [showOnboarding, setShowOnboarding] = useState(false);

    // Use useMemo to prevent recreation of these objects on every render
  const songsRef = useMemo(() => collection(db, "songs"), []);
  const functions = useMemo(() => getFunctions(), []);

  // 🆕 UPDATED: Handle request toggle using HTTP function (CORS-safe) - DEBUG VERSION
  const handleToggleRequests = async () => {
    console.log("🔄 TOGGLE DEBUG: Starting toggle function");
    console.log("🔄 TOGGLE DEBUG: Current acceptingRequests state:", acceptingRequests);
    console.log("🔄 TOGGLE DEBUG: Will send to backend:", !acceptingRequests);
    
    setToggleLoading(true);
    try {
      // Get the user's auth token
      const idToken = await user.getIdToken();
      
      const newState = !acceptingRequests;
      console.log("🔄 TOGGLE DEBUG: New state to send:", newState);
      
      // Use HTTP version to avoid CORS issues - following the established pattern
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/toggleAcceptingRequestsHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          acceptingRequests: newState
        })
      });

      console.log("🔄 TOGGLE DEBUG: Response status:", response.status);
      console.log("🔄 TOGGLE DEBUG: Response ok:", response.ok);

      if (!response.ok) {
        // Check if it's a CORS error or other HTTP error
        if (response.status === 0) {
          throw new Error('CORS error - function routing issue detected');
        }
        const errorText = await response.text();
        console.log("🔄 TOGGLE DEBUG: Error response text:", errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const result = await response.json();
      console.log("🔄 TOGGLE DEBUG: Backend response:", result);
      console.log("🔄 TOGGLE DEBUG: Backend returned acceptingRequests:", result.acceptingRequests);
      console.log("🔄 TOGGLE DEBUG: Backend returned success:", result.success);
      console.log("🔄 TOGGLE DEBUG: Backend returned message:", result.message);
      
      if (result.success) {
        console.log("🔄 TOGGLE DEBUG: Setting local state to:", result.acceptingRequests);
        console.log("🔄 TOGGLE DEBUG: Previous state was:", acceptingRequests);
        
        setAcceptingRequests(result.acceptingRequests);
        
        // Let's verify the state after a short delay
        setTimeout(() => {
          console.log("🔄 TOGGLE DEBUG: State after 100ms delay:", acceptingRequests);
        }, 100);
        
        setTimeout(() => {
          console.log("🔄 TOGGLE DEBUG: State after 500ms delay:", acceptingRequests);
        }, 500);
        
        alert(result.message);
      } else {
        console.log("🔄 TOGGLE DEBUG: Backend returned success=false");
        throw new Error(result.message || "Toggle failed");
      }
    } catch (error) {
      console.error("🔄 TOGGLE DEBUG: Error occurred:", error);
      console.error("🔄 TOGGLE DEBUG: Error type:", typeof error);
      console.error("🔄 TOGGLE DEBUG: Error message:", error.message);
      console.error("🔄 TOGGLE DEBUG: Error stack:", error.stack);
      
      // Provide specific error messages based on the error type
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        alert("⚠️ CORS Error Detected\n\nThis is a known Firebase Functions routing issue. The function needs to be updated with proper CORS configuration.\n\nPlease contact the development team to implement the HTTP version with CORS headers.");
      } else {
        alert("Error updating request status: " + error.message);
      }
    } finally {
      setToggleLoading(false);
      console.log("🔄 TOGGLE DEBUG: Toggle function completed");
      console.log("🔄 TOGGLE DEBUG: Final acceptingRequests state:", acceptingRequests);
    }
  };

  // FIXED useEffect with proper dependencies
  useEffect(() => {
    const fetchData = async () => {
      setProfileLoading(true);
      setProfileError(null);
      
      try {
        // Wait for authentication to be fully ready
        if (!user || !user.uid) {
          console.error("No authenticated user found");
          setProfileError("Please log in to access your dashboard");
          return;
        }

        // Wait a bit more to ensure Firebase auth is fully loaded
        await new Promise(resolve => setTimeout(resolve, 1000));

        console.log("Authenticated user:", user.uid);
        console.log("User object:", { uid: user.uid, email: user.email });

        // Test auth first with debug function
        console.log("Testing authentication with debug function...");
        try {
          const debugAuth = httpsCallable(functions, 'debugAuth');
          const debugResult = await debugAuth();
          console.log("Debug auth result:", debugResult.data);
          console.log("Debug auth details:", debugResult.data.debug);
          
          if (!debugResult.data.debug.hasAuth) {
            console.log("Debug result:", debugResult.data);
            throw new Error("Authentication context not available in Cloud Function");
          }
        } catch (debugError) {
          console.error("Debug auth failed:", debugError);
          throw new Error("Authentication setup issue: " + debugError.message);
        }

        // Now try the real function
        console.log("Fetching private musician data securely...");
        const getMusicianPrivateData = httpsCallable(functions, 'getMusicianPrivateData');
        
        const result = await getMusicianPrivateData();
        console.log("Private data function result:", result);
        
        if (result.data && result.data.success) {
          const privateData = result.data.data;
          console.log("Private data loaded securely");
          
          // 🔒 SECURITY FIX: Remove sensitive data logging - only log safe metadata
          const safeLogData = {
            hasDisplayName: !!privateData.displayName,
            hasPhoneNumber: !!privateData.phoneNumber,
            hasEmail: !!privateData.email,
            hasRealName: !!privateData.realName,
            averageRating: privateData.averageRating,
            totalRatings: privateData.totalRatings,
            totalEarnings: privateData.totalEarnings,
            isActive: privateData.isActive,
            acceptingRequests: privateData.acceptingRequests,
            onboardingCompleted: privateData.onboardingCompleted
          };
          console.log("✅ Private data metadata (safe to log):", safeLogData);
          
          setDisplayName(privateData.displayName || "");
          setPhoneNumber(privateData.phoneNumber || "");
          setEmail(privateData.email || ""); // NEW
          setRealName(privateData.realName || ""); // NEW
          setOriginalDisplayName(privateData.displayName || "");
          setOriginalPhoneNumber(privateData.phoneNumber || "");
          setOriginalEmail(privateData.email || ""); // NEW
          setOriginalRealName(privateData.realName || ""); // NEW
          
          // 🆕 NEW: Set accepting requests status
          setAcceptingRequests(privateData.acceptingRequests !== false); // Default to true
          
          // Check onboarding status - be more strict about completion
          const hasCompletedOnboarding = privateData.onboardingCompleted === true; // Must be explicitly true
          
          // For new users: if onboardingCompleted is undefined/false, they need onboarding
          // regardless of having some default profile data
          const shouldConsiderComplete = hasCompletedOnboarding;
          
          setOnboardingCompleted(shouldConsiderComplete);
          
          // Show onboarding if not explicitly completed
          const needsOnboarding = !shouldConsiderComplete;
          setShowOnboarding(needsOnboarding);
          
          console.log("Onboarding status:", { 
            backendOnboardingFlag: privateData.onboardingCompleted,
            hasDisplayName: !!privateData.displayName,
            hasPhone: !!privateData.phoneNumber,
            hasEmail: !!privateData.email, // NEW
            hasRealName: !!privateData.realName, // NEW
            shouldConsiderComplete: shouldConsiderComplete,
            needsOnboarding: needsOnboarding,
            willShowOnboarding: needsOnboarding
          });
        } else {
          throw new Error(result.data?.message || "Failed to load profile data");
        }
      } catch (error) {
        console.error("Error loading private data:", error);
        
        // Better error handling
        if (error.code === 'functions/unauthenticated') {
          setProfileError("Authentication issue. Please refresh the page and try again.");
        } else if (error.code === 'functions/not-found') {
          setProfileError("Musician profile not found. Please complete your profile setup.");
          // If profile not found, definitely show onboarding
          setOnboardingCompleted(false);
          setShowOnboarding(true);
        } else if (error.message.includes("Authentication context")) {
          setProfileError("Authentication setup issue. Please refresh the page.");
        } else {
          setProfileError("Failed to load profile data: " + error.message);
        }
        
        // Clear sensitive data on error
        setDisplayName("");
        setPhoneNumber("");
        setEmail(""); // NEW
        setRealName(""); // NEW
        setOriginalDisplayName("");
        setOriginalPhoneNumber("");
        setOriginalEmail(""); // NEW
        setOriginalRealName(""); // NEW
      } finally {
        setProfileLoading(false);
      }

      // Fetch songs (public data) - this should work
      try {
        console.log("Fetching songs...");
        const q = query(songsRef, where("musicianId", "==", user.uid));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSongs(list);
        console.log("Songs loaded:", list.length);
      } catch (error) {
        console.error("Error loading songs:", error);
        // Songs might fail due to Firestore rules - that's OK for now
      }
    };

    if (user) {
      fetchData();
    } else {
      setProfileLoading(false);
      setProfileError("Please log in to access your dashboard");
    }
  }, [user, functions, songsRef]); // Include 'user' since we check 'if (user)'

  // Update verification status when props change
  useEffect(() => {
    if (propVerificationStatus) {
      setVerificationStatus(propVerificationStatus);
    }
  }, [propVerificationStatus]);

  const fetchSongs = async () => {
    const q = query(songsRef, where("musicianId", "==", user.uid));
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setSongs(list);
  };

  const handleAddSong = async () => {
    if (!newSongName.trim() || !newSongArtist.trim()) return;

    const priceValue = parseFloat(newSongPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      alert("Please enter a valid non-negative number for price.");
      return;
    }

    try {
      await addDoc(songsRef, {
        name: newSongName.trim(),
        artist: newSongArtist.trim(),
        musicianId: user.uid,
        createdAt: new Date(),
        price: priceValue
      });

      setNewSongName("");
      setNewSongArtist("");
      setNewSongPrice("");
      fetchSongs();
      console.log("Song added successfully");
    } catch (error) {
      console.error("Error adding song:", error);
      alert("Error adding song. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "songs", id));
      fetchSongs();
      console.log("Song deleted successfully");
    } catch (error) {
      console.error("Error deleting song:", error);
      alert("Error deleting song. Please try again.");
    }
  };

  // UPDATED: Secure profile save using backend function - now includes email and realName
  const handleSaveProfile = async () => {
    const trimmedName = displayName.trim();
    const trimmedPhone = phoneNumber.trim();
    const trimmedEmail = email.trim(); // NEW
    const trimmedRealName = realName.trim(); // NEW
    
    // Validate phone number
    if (trimmedPhone && !trimmedPhone.match(/^\+?[1-9]\d{1,14}$/)) {
      alert("Please enter a valid phone number (e.g., +61412345678 or +12345678901)");
      return;
    }
    
    // NEW: Validate email format
    if (trimmedEmail && !trimmedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert("Please enter a valid email address");
      return;
    }
    
    // NEW: Validate real name
    if (trimmedRealName && trimmedRealName.length < 2) {
      alert("Real name must be at least 2 characters long");
      return;
    }
    
    // Check if any field has changed
    if ((trimmedName && trimmedName !== originalDisplayName) || 
        (trimmedPhone !== originalPhoneNumber) ||
        (trimmedEmail !== originalEmail) || // NEW
        (trimmedRealName !== originalRealName)) { // NEW
      
      try {
        const updateMusicianProfile = httpsCallable(functions, 'updateMusicianProfile');
        
        console.log("Updating profile securely...");
        const result = await updateMusicianProfile({
          displayName: trimmedName,
          phoneNumber: trimmedPhone,
          email: trimmedEmail, // NEW
          realName: trimmedRealName // NEW
        });
        
        if (result.data.success) {
          setOriginalDisplayName(trimmedName);
          setOriginalPhoneNumber(trimmedPhone);
          setOriginalEmail(trimmedEmail); // NEW
          setOriginalRealName(trimmedRealName); // NEW
          alert("Profile updated successfully!");
          console.log("Profile updated securely");
        } else {
          throw new Error(result.data.message || "Update failed");
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        alert("Error updating profile: " + error.message);
      }
    }
  };

  // Secure SMS test using backend function
  const handleTestSMS = async () => {
    if (!phoneNumber.trim()) {
      alert("Please add a phone number to your profile first.");
      return;
    }

    try {
      const testSMS = httpsCallable(functions, 'testSMS');
      
      console.log("Sending test SMS securely...");
      const result = await testSMS();
      
      if (result.data.success) {
        alert("Test SMS sent successfully! Check your phone.");
        console.log("Test SMS sent securely");
      } else {
        throw new Error(result.data.message || "SMS failed");
      }
    } catch (error) {
      console.error("Error sending test SMS:", error);
      
      if (error.code === 'functions/failed-precondition') {
        alert("Please add a phone number to your profile first.");
      } else if (error.code === 'functions/resource-exhausted') {
        alert("Too many test SMS requests. Please try again later.");
      } else {
        alert("Error sending test SMS: " + error.message);
      }
    }
  };

  // Handle onboarding completion - FIXED
  const handleOnboardingComplete = async (onboardingData) => {
    try {
      const completeOnboarding = async (data) => {
        const idToken = await auth.currentUser.getIdToken();
        
        const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/completeOnboardingHttp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(data)
        });

        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Failed to complete onboarding');
        }
        
        return result;
      };
      
      console.log("Completing onboarding securely...");
      console.log("Onboarding data being sent:", onboardingData);
      
      const result = await completeOnboarding({
        realName: onboardingData.realName,    
        displayName: onboardingData.displayName,
        phoneNumber: onboardingData.phoneNumber,
        bio: onboardingData.bio,
        genres: onboardingData.genres,
        experience: onboardingData.experience,
        selectedCharacter: onboardingData.selectedCharacter,
        songs: onboardingData.songs
      });
      
      console.log("Backend response:", result);
      console.log("Backend response success:", result.success);
      console.log("Backend response details:", result);
      
      // FIX: Check for success directly on result object, not result.data.success
      if (result.success) {
        console.log("✅ Onboarding completed successfully - setting local state");
        
        // IMPORTANT: Set onboarding as completed to prevent popup from showing again
        setOnboardingCompleted(true);
        setShowOnboarding(false);
        
        // Update local state with the new data
        setDisplayName(onboardingData.displayName);
        setPhoneNumber(onboardingData.phoneNumber);
        setRealName(onboardingData.realName || ""); // NEW: Update real name from onboarding
        setEmail(onboardingData.email || user?.email || ""); // NEW: Update email
        setOriginalDisplayName(onboardingData.displayName);
        setOriginalPhoneNumber(onboardingData.phoneNumber);
        setOriginalRealName(onboardingData.realName || ""); // NEW
        setOriginalEmail(onboardingData.email || user?.email || ""); // NEW
        
        // CLEAR existing songs to prevent duplicates, then add the new ones
        setSongs([]);
        
        // Add the songs from onboarding directly to state to prevent duplicates
        const newSongs = onboardingData.songs.map((song, index) => ({
          id: `onboarding-${index}-${Date.now()}`, // temporary ID
          name: song.name,
          artist: song.artist,
          price: song.price,
          musicianId: user.uid,
          createdAt: new Date()
        }));
        setSongs(newSongs);
        
        // Show success message
        alert("🎉 Welcome aboard! Your musician profile has been created successfully!");
        
        console.log("✅ Local onboarding state set to true");
        console.log("✅ Songs added to local state:", newSongs.length);
      } else {
        throw new Error(result.message || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
      console.error("Full error details:", error);
      
      // More specific error handling
      if (error.message.includes('Failed to complete onboarding')) {
        alert("Error completing onboarding: " + error.message + "\n\nPlease try again or contact support if the issue persists.");
      } else {
        alert("Error completing onboarding: " + error.message);
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      if (onSignOut) {
        onSignOut();
      }
    }
  };

  const startEditing = (song) => {
    setEditingSongId(song.id);
    setEditingSongData({
      name: song.name,
      artist: song.artist,
      price: song.price.toString()
    });
  };

  const cancelEditing = () => {
    setEditingSongId(null);
    setEditingSongData({ name: "", artist: "", price: "" });
  };

  const handleEditChange = (field, value) => {
    setEditingSongData(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    const { name, artist, price } = editingSongData;
    if (!name.trim() || !artist.trim()) {
      alert("Song name and artist cannot be empty.");
      return;
    }
    const priceValue = parseFloat(price);
    if (isNaN(priceValue) || priceValue < 0) {
      alert("Please enter a valid non-negative number for price.");
      return;
    }

    try {
      await updateDoc(doc(db, "songs", editingSongId), {
        name: name.trim(),
        artist: artist.trim(),
        price: priceValue
      });

      cancelEditing();
      fetchSongs();
      console.log("Song updated successfully");
    } catch (error) {
      console.error("Error updating song:", error);
      alert("Error updating song. Please try again.");
    }
  };

  const handleDownloadQRCode = () => {
    const canvas = document.getElementById("qr-code");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `RequestASong-QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleVerificationUpdate = (newStatus) => {
    setVerificationStatus(newStatus);
  };

  // Show onboarding if needed
  if (showOnboarding) {
    return (
      <MusicianOnboarding 
        user={user}
        onComplete={handleOnboardingComplete}
        onSkip={() => setShowOnboarding(false)}
        existingData={{
          displayName: displayName,
          phoneNumber: phoneNumber,
          email: email || user?.email, // NEW: Include email
          realName: realName // NEW: Include real name
        }}
      />
    );
  }

  // Updated tabs array to include rankings and referrals
  const tabs = [
    { id: "earnings", label: "Earnings", icon: "💰", bgColor: "#059669", hoverColor: "#047857" },
    { id: "profile", label: "Profile", icon: "👤", bgColor: "#3B82F6", hoverColor: "#2563EB" },
    { id: "songs", label: "Songs", icon: "🎵", bgColor: "#7C3AED", hoverColor: "#6D28D9" },
    { id: "referrals", label: "Referrals", icon: "💎", bgColor: "#10B981", hoverColor: "#059669" },
    { id: "rankings", label: "Rankings", icon: "🏆", bgColor: "#F59E0B", hoverColor: "#D97706" },
    { id: "qrcode", label: "QR & Print", icon: "📱", bgColor: "#4F46E5", hoverColor: "#4338CA" }
  ];

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#111827',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isTablet = typeof window !== 'undefined' && window.innerWidth < 768;

  const headerStyle = {
    backgroundColor: '#1F2937',
    borderBottom: '8px solid #7C3AED',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  const avatarStyle = {
    height: isMobile ? '80px' : '96px',
    width: isMobile ? '80px' : '96px',
    borderRadius: '50%',
    backgroundColor: '#7C3AED',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '8px solid #6D28D9',
    position: 'relative'
  };

  const liveIndicatorStyle = {
    position: 'absolute',
    bottom: '-8px',
    right: '-8px',
    height: isMobile ? '24px' : '32px',
    width: isMobile ? '24px' : '32px',
    backgroundColor: '#10B981',
    borderRadius: '50%',
    border: '4px solid white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const cardStyle = {
    backgroundColor: '#1F2937',
    border: '8px solid #7C3AED',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden'
  };

  const inputStyle = {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: '#374151',
    border: '4px solid #4B5563',
    borderRadius: '16px',
    padding: '12px 16px',
    color: 'white',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .pulse { animation: pulse 2s infinite; }
        `}
      </style>

      {/* Dashboard Tour for First-Time Users */}
      <MusicianDashboardTour 
        onComplete={(completed) => {
          if (completed) {
            console.log('Tour completed successfully!');
            // Could trigger confetti or success message here
          }
        }}
        onTabChange={(tabId) => {
          setActiveTab(tabId);
        }}
      />

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header - Mobile Responsive */}
        <div style={headerStyle}>
          <div style={{ padding: isMobile ? '20px 16px 30px' : '32px 32px 40px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? '16px' : '24px', 
              flexDirection: isMobile ? 'column' : 'row', 
              textAlign: isMobile ? 'center' : 'left' 
            }}>
              <div style={avatarStyle}>
                <img 
                  src={starLogo} 
                  alt="RequestASong Star Logo" 
                  style={{ 
                    width: isMobile ? '48px' : '64px', 
                    height: isMobile ? '48px' : '64px',
                    objectFit: 'contain'
                  }} 
                />
              </div>
              <div>
                <h1 style={{ 
                  fontSize: isMobile ? '24px' : isTablet ? '32px' : '48px', 
                  fontWeight: 'bold', 
                  margin: '0 0 8px 0',
                  lineHeight: '1.2'
                }}>
                  Welcome back, <span style={{ color: '#C4B5FD' }}>{displayName || user?.email || "Musician"}</span>!
                </h1>
                <p style={{ 
                  color: '#C4B5FD', 
                  fontSize: isMobile ? '14px' : '20px', 
                  margin: 0 
                }}>Your musical journey continues here</p>
                {profileLoading && (
                  <p style={{ color: '#FBBF24', fontSize: '14px', margin: '8px 0 0 0' }}>
                    🔒 Loading profile securely...
                  </p>
                )}
                {profileError && (
                  <p style={{ color: '#EF4444', fontSize: '14px', margin: '8px 0 0 0' }}>
                    ⚠️ {profileError}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Centered Onboarding Popup */}
        {onboardingCompleted === false && !showOnboarding && (
          <div style={{ 
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{ 
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              padding: '32px',
              backgroundColor: '#7C3AED',
              borderRadius: '24px',
              border: '4px solid #6D28D9',
              boxShadow: '0 10px 25px rgba(124, 58, 237, 0.3)'
            }}>
              <div style={{ marginBottom: '16px', fontSize: '48px' }}>🚀</div>
              <h3 style={{ 
                color: 'white', 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 12px 0' 
              }}>
                Ready to Start Earning?
              </h3>
              <p style={{ 
                color: '#C4B5FD', 
                fontSize: '16px', 
                margin: '0 0 24px 0' 
              }}>
                Complete your quick setup to start receiving song requests and earning money!
              </p>
              <button 
                onClick={() => setShowOnboarding(true)}
                style={{
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  padding: '16px 32px',
                  borderRadius: '16px',
                  border: '4px solid #D97706',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(245, 158, 11, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#D97706';
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 12px 30px rgba(245, 158, 11, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#F59E0B';
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.4)';
                }}
              >
                🎸 Complete Setup Guide
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation - Updated for 5 tabs */}
        <div style={{ backgroundColor: '#1F2937', borderBottom: '2px solid #374151' }}>
          <div style={{ padding: '16px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', 
              gap: '12px',
              maxWidth: isMobile ? '600px' : '1000px',
              margin: '0 auto'
            }}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: isMobile ? '16px 12px' : '20px 16px',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '14px' : '16px',
                    transition: 'all 0.3s ease',
                    backgroundColor: activeTab === tab.id ? tab.bgColor : '#374151',
                    color: activeTab === tab.id ? 'white' : '#9CA3AF',
                    border: activeTab === tab.id ? '3px solid white' : '3px solid transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '16px',
                    boxShadow: activeTab === tab.id ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transform: activeTab === tab.id ? 'scale(1.05)' : 'scale(1)',
                    minHeight: '80px',
                    justifyContent: 'center'
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = 'white';
                      e.target.style.backgroundColor = '#4B5563';
                      e.target.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== tab.id) {
                      e.target.style.color = '#9CA3AF';
                      e.target.style.backgroundColor = '#374151';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <span style={{ fontSize: isMobile ? '28px' : '32px' }}>{tab.icon}</span>
                  <span style={{ textAlign: 'center', lineHeight: '1.2' }}>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content - Mobile Responsive */}
        <div style={{ padding: isMobile ? '16px' : '32px' }}>
          {activeTab === "earnings" && (
            <div>
              <div className="earnings-overview">
                <EarningsDashboard user={user} />
              </div>
              
              {/* Quick Actions Section for Tour */}
              <div className="quick-actions" style={{
                backgroundColor: '#1F2937',
                borderRadius: '12px',
                padding: '24px',
                marginTop: '24px',
                border: '2px solid #374151'
              }}>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  margin: '0 0 16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⚡ Quick Actions
                </h3>
                
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <button
                    className="accept-requests-toggle quick-action-btn"
                    onClick={() => {/* Toggle logic here */}}
                    style={{
                      backgroundColor: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    🎵 Toggle Request Acceptance
                  </button>
                  
                  <button
                    className="quick-action-btn"
                    onClick={() => {/* Navigate to profile */}}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    📝 Complete Profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add the rankings tab content */}
          {activeTab === "rankings" && (
            <div className="gamification-progress">
              <MusicianGamification user={user} />
            </div>
          )}

          {/* Add the referrals tab content */}
          {activeTab === "referrals" && (
            <div className="referral-section">
              {/* Debug component removed for production */}
              <ReferralDashboard user={user} />
            </div>
          )}

          {activeTab === "profile" && (
            <div className="account-verification" style={{ maxWidth: '600px' }}>
              {/* Show Verification Section at top only if NOT fully verified */}
              {(!verificationStatus.emailVerified || !verificationStatus.phoneVerified) && (
                <VerificationStatusSection 
                  user={user}
                  userProfile={userProfile}
                  verificationStatus={verificationStatus}
                  phoneNumber={phoneNumber}
                  onVerificationUpdate={handleVerificationUpdate}
                />
              )}
              {/* UPDATED: Profile Settings Card with Email and Real Name */}
              <div style={cardStyle}>
                <div style={{ padding: '32px', borderBottom: '4px solid #6D28D9', backgroundColor: '#581C87' }}>
                  <h2 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', margin: 0 }}>
                    <span style={{ marginRight: '16px' }}>🔒</span>
                    Secure Profile Settings
                  </h2>
                  <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                    Your sensitive data is protected by secure backend functions
                  </p>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '32px' }}>
                  {profileError && (
                    <div style={{
                      backgroundColor: '#7F1D1D',
                      border: '4px solid #EF4444',
                      borderRadius: '16px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <p style={{ color: '#FEE2E2', margin: 0, fontWeight: 'bold' }}>
                        ⚠️ {profileError}
                      </p>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {/* NEW: Real Name Field */}
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', fontSize: '18px', marginBottom: '12px' }}>
                        Real Name (Private)
                      </label>
                      <input
                        type="text"
                        value={realName}
                        onChange={(e) => setRealName(e.target.value)}
                        style={inputStyle}
                        placeholder="Your legal/real name"
                        disabled={profileLoading}
                        onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                        onBlur={(e) => e.target.style.borderColor = '#4B5563'}
                      />
                      <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                        🔒 Used for legal and verification purposes only - never shown publicly
                      </p>
                    </div>

                    {/* NEW: Email Field */}
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', fontSize: '18px', marginBottom: '12px' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={inputStyle}
                        placeholder="your.email@example.com"
                        disabled={profileLoading}
                        onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                        onBlur={(e) => e.target.style.borderColor = '#4B5563'}
                      />
                      <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                        📧 Used for account management and important notifications
                      </p>
                    </div>

                    {/* UPDATED: Stage Name Field (formerly Display Name) */}
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', fontSize: '18px', marginBottom: '12px' }}>
                        Stage Name (Public)
                      </label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        style={inputStyle}
                        placeholder="Your stage/performer name"
                        disabled={profileLoading}
                        onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                        onBlur={(e) => e.target.style.borderColor = '#4B5563'}
                      />
                      <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                        🎤 This is how fans will see your name when requesting songs
                      </p>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', fontSize: '18px', marginBottom: '12px' }}>
                        Phone Number (Secure)
                      </label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        style={inputStyle}
                        placeholder="+61412345678 (include country code)"
                        disabled={profileLoading}
                        onFocus={(e) => e.target.style.borderColor = '#7C3AED'}
                        onBlur={(e) => e.target.style.borderColor = '#4B5563'}
                      />
                      <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '16px', margin: '8px 0 0 0' }}>
                        🔒 Required for SMS notifications - stored securely on server only
                      </p>
                    </div>

                    {/* 🆕 NEW: Request Toggle Section */}
                    <div>
                      <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', fontSize: '18px', marginBottom: '12px' }}>
                        Song Request Status
                      </label>
                      
                      <div style={{
                        backgroundColor: acceptingRequests ? '#065F46' : '#7F1D1D',
                        border: `4px solid ${acceptingRequests ? '#10B981' : '#EF4444'}`,
                        borderRadius: '16px',
                        padding: '20px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                              <span style={{ fontSize: '32px' }}>
                                {acceptingRequests ? '✅' : '🚫'}
                              </span>
                              <h4 style={{ 
                                color: 'white', 
                                fontWeight: 'bold', 
                                fontSize: '20px', 
                                margin: 0 
                              }}>
                                {acceptingRequests ? 'Accepting Requests' : 'Requests Disabled'}
                              </h4>
                            </div>
                            <p style={{ 
                              color: acceptingRequests ? '#A7F3D0' : '#FCA5A5', 
                              fontSize: '16px', 
                              margin: 0,
                              lineHeight: '1.4'
                            }}>
                              {acceptingRequests 
                                ? 'Fans can request songs and make payments. You\'ll receive SMS notifications.' 
                                : 'Song requests are disabled. Fans cannot request songs or make payments.'
                              }
                            </p>
                          </div>
                          
                          <button
                            onClick={handleToggleRequests}
                            disabled={toggleLoading || profileLoading}
                            style={{
                              backgroundColor: acceptingRequests ? '#EF4444' : '#059669',
                              color: 'white',
                              fontWeight: 'bold',
                              padding: '12px 24px',
                              borderRadius: '12px',
                              border: `4px solid ${acceptingRequests ? '#DC2626' : '#10B981'}`,
                              fontSize: '16px',
                              cursor: toggleLoading || profileLoading ? 'not-allowed' : 'pointer',
                              transition: 'all 0.3s ease',
                              opacity: toggleLoading || profileLoading ? 0.5 : 1,
                              minWidth: '140px'
                            }}
                            onMouseOver={(e) => {
                              if (!toggleLoading && !profileLoading) {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.3)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!toggleLoading && !profileLoading) {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.boxShadow = 'none';
                              }
                            }}
                          >
                            {toggleLoading ? '⏳ Updating...' : 
                             acceptingRequests ? '🚫 Disable Requests' : '✅ Enable Requests'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Info box */}
                      <div style={{
                        backgroundColor: '#1E3A8A',
                        border: '4px solid #3B82F6',
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <h5 style={{ color: '#BFDBFE', fontWeight: 'bold', fontSize: '16px', margin: '0 0 8px 0' }}>
                          💡 When to disable requests:
                        </h5>
                        <ul style={{ color: '#BFDBFE', fontSize: '14px', lineHeight: '1.5', margin: 0, paddingLeft: '20px' }}>
                          <li>When you're not performing or taking a break</li>
                          <li>Near the end of your set when you want to stop taking new requests</li>
                          <li>If you're getting spammed or receiving inappropriate requests</li>
                          <li>During sound checks or between sets</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <button 
                        onClick={handleSaveProfile}
                        disabled={profileLoading}
                        style={{
                          backgroundColor: profileLoading ? '#6B7280' : '#3B82F6',
                          color: 'white',
                          fontWeight: 'bold',
                          padding: '16px 32px',
                          borderRadius: '16px',
                          border: '4px solid #2563EB',
                          fontSize: '20px',
                          cursor: profileLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseOver={(e) => {
                          if (!profileLoading) {
                            e.target.style.backgroundColor = '#2563EB';
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.3)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!profileLoading) {
                            e.target.style.backgroundColor = '#3B82F6';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                          }
                        }}
                      >
                        {profileLoading ? '🔒 Loading...' : '🔒 Save Profile Securely'}
                      </button>

                      {phoneNumber && !profileLoading && (
                        <button 
                          onClick={handleTestSMS}
                          style={{
                            backgroundColor: '#7C3AED',
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            border: '4px solid #6D28D9',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#6D28D9';
                            e.target.style.transform = 'scale(1.02)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#7C3AED';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          📱 Test SMS Notifications
                        </button>
                      )}
                    </div>

                    {phoneNumber && !profileLoading ? (
                      <div style={{
                        backgroundColor: '#065F46',
                        border: '4px solid #10B981',
                        borderRadius: '16px',
                        padding: '24px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '32px' }}>✅</span>
                          <div>
                            <p style={{ color: '#A7F3D0', fontWeight: 'bold', fontSize: '18px', margin: '0 0 4px 0' }}>SMS notifications enabled!</p>
                            <p style={{ color: '#6EE7B7', fontSize: '16px', margin: 0 }}>You'll receive secure notifications when someone requests a song.</p>
                          </div>
                        </div>
                      </div>
                    ) : !profileLoading ? (
                      <div style={{
                        backgroundColor: '#92400E',
                        border: '4px solid #F59E0B',
                        borderRadius: '16px',
                        padding: '24px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '32px' }}>⚠️</span>
                          <div>
                            <p style={{ color: '#FEF3C7', fontWeight: 'bold', fontSize: '18px', margin: '0 0 4px 0' }}>Add your phone number</p>
                            <p style={{ color: '#FCD34D', fontSize: '16px', margin: 0 }}>Enable secure SMS notifications for song requests.</p>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Logout Section */}
                    <div style={{
                      backgroundColor: '#7F1D1D',
                      border: '4px solid #EF4444',
                      borderRadius: '16px',
                      padding: '24px',
                      textAlign: 'center'
                    }}>
                      <h4 style={{ color: '#FEE2E2', fontWeight: 'bold', fontSize: '18px', margin: '0 0 16px 0' }}>
                        🚪 Account Actions
                      </h4>
                      <p style={{ color: '#FEE2E2', fontSize: '14px', margin: '0 0 20px 0' }}>
                        Need to switch accounts or take a break?
                      </p>
                      <button
                        onClick={handleLogout}
                        style={{
                          backgroundColor: '#EF4444',
                          color: 'white',
                          fontWeight: 'bold',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          border: '3px solid #DC2626',
                          fontSize: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#DC2626';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#EF4444';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        🚪 Logout
                      </button>
                    </div>

                    {/* Security Notice */}
                    <div style={{
                      backgroundColor: '#1E3A8A',
                      border: '4px solid #3B82F6',
                      borderRadius: '16px',
                      padding: '20px'
                    }}>
                      <h4 style={{ color: '#BFDBFE', fontWeight: 'bold', fontSize: '16px', margin: '0 0 12px 0' }}>
                        🔒 Security & Privacy
                      </h4>
                      <ul style={{ color: '#BFDBFE', fontSize: '14px', lineHeight: '1.5', margin: 0, paddingLeft: '20px' }}>
                        <li>Your real name and email are encrypted and stored securely on our servers</li>
                        <li>Real name is never exposed to the public or other users</li>
                        <li>Email is only used for account management and important notifications</li>
                        <li>Phone number is only used for sending you song request notifications</li>
                        <li>All profile updates use secure backend functions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Show Verification Section at bottom when FULLY verified */}
              {verificationStatus.emailVerified && verificationStatus.phoneVerified && (
                <div style={{ marginTop: '24px' }}>
                  <VerificationStatusSection 
                    user={user}
                    userProfile={userProfile}
                    verificationStatus={verificationStatus}
                    phoneNumber={phoneNumber}
                    onVerificationUpdate={handleVerificationUpdate}
                  />
                </div>
              )}

              {/* Quick Actions Section for Tour Step 7 */}
              <div className="quick-actions" style={{
                backgroundColor: '#1F2937',
                borderRadius: '12px',
                padding: '24px',
                marginTop: '24px',
                border: '2px solid #374151'
              }}>
                <h3 style={{ 
                  color: 'white', 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  margin: '0 0 16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⚡ Quick Actions
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <button
                    className="accept-requests-toggle quick-action-btn"
                    onClick={() => {/* Toggle logic here */}}
                    style={{
                      backgroundColor: acceptingRequests ? '#10B981' : '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}
                  >
                    {acceptingRequests ? '✅ Accepting Requests' : '❌ Not Accepting Requests'}
                  </button>
                  
                  <button
                    className="quick-action-btn"
                    onClick={() => setActiveTab('songs')}
                    style={{
                      backgroundColor: '#7C3AED',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}
                  >
                    🎵 Manage Songs
                  </button>
                  
                  <button
                    className="quick-action-btn"
                    onClick={() => setActiveTab('qrcode')}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center'
                    }}
                  >
                    📱 Get QR Code
                  </button>
                </div>

                <p style={{ 
                  color: '#9CA3AF', 
                  fontSize: '14px', 
                  marginTop: '16px',
                  fontStyle: 'italic'
                }}>
                  Quick access to your most important musician tools
                </p>
              </div>
            </div>
          )}

          {activeTab === "songs" && (
            <div className="song-library">
              <div style={cardStyle}>
                <div style={{ padding: '32px', borderBottom: '4px solid #6D28D9', backgroundColor: '#581C87' }}>
                  <h2 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', margin: 0 }}>
                    <span style={{ marginRight: '16px' }}>🎵</span>
                    Manage Your Songs
                  </h2>
                  <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                    Add songs that fans can request
                  </p>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '32px' }}>
                  {/* Bulk Upload Button */}
                  <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                    <BulkSongUpload 
                      user={user}
                      existingSongs={songs}
                      onUploadComplete={fetchSongs}
                    />
                  </div>

                  {/* Add New Song Form */}
                  <div style={{ marginBottom: '32px', padding: '24px', backgroundColor: '#374151', borderRadius: '16px', border: '4px solid #4B5563' }}>
                    <h3 style={{ color: 'white', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', margin: '0 0 20px 0' }}>Add New Song</h3>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 120px auto', gap: '16px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', marginBottom: '8px' }}>Song Name</label>
                        <input
                          className="song-name-input"
                          type="text"
                          value={newSongName}
                          onChange={(e) => setNewSongName(e.target.value)}
                          placeholder="Enter song name"
                          style={inputStyle}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', marginBottom: '8px' }}>Artist</label>
                        <input
                          className="artist-input"
                          type="text"
                          value={newSongArtist}
                          onChange={(e) => setNewSongArtist(e.target.value)}
                          placeholder="Enter artist name"
                          style={inputStyle}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', color: '#C4B5FD', fontWeight: 'bold', marginBottom: '8px' }}>Price ($)</label>
                        <input
                          className="price-input"
                          type="number"
                          value={newSongPrice}
                          onChange={(e) => setNewSongPrice(e.target.value)}
                          placeholder="15.00"
                          min="0"
                          step="0.50"
                          style={inputStyle}
                        />
                      </div>
                      
                      <button
                        className="add-new-song-btn"
                        onClick={handleAddSong}
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          fontWeight: 'bold',
                          padding: '16px 24px',
                          borderRadius: '12px',
                          border: '4px solid #10B981',
                          fontSize: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          minWidth: isMobile ? '100%' : 'auto'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#047857';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#059669';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Add Song
                      </button>
                    </div>
                  </div>

                  {/* Songs List */}
                  {songs.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                      {songs.map((song) => (
                        <div key={song.id} style={{
                          backgroundColor: '#374151',
                          border: '4px solid #4B5563',
                          borderRadius: '16px',
                          padding: '20px',
                          transition: 'all 0.3s ease'
                        }}>
                          {editingSongId === song.id ? (
                            /* Edit Mode */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <input
                                type="text"
                                value={editingSongData.name}
                                onChange={(e) => handleEditChange('name', e.target.value)}
                                style={inputStyle}
                                placeholder="Song name"
                              />
                              <input
                                type="text"
                                value={editingSongData.artist}
                                onChange={(e) => handleEditChange('artist', e.target.value)}
                                style={inputStyle}
                                placeholder="Artist name"
                              />
                              <input
                                type="number"
                                value={editingSongData.price}
                                onChange={(e) => handleEditChange('price', e.target.value)}
                                style={inputStyle}
                                placeholder="Price"
                                min="0"
                                step="0.50"
                              />
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={saveEdit}
                                  style={{
                                    backgroundColor: '#059669',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    flex: 1
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  style={{
                                    backgroundColor: '#6B7280',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    flex: 1
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Display Mode */
                            <div>
                              <h4 style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', margin: '0 0 8px 0' }}>{song.name}</h4>
                              <p style={{ color: '#C4B5FD', fontSize: '16px', margin: '0 0 8px 0' }}>by {song.artist}</p>
                              <p style={{ color: '#10B981', fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0' }}>${song.price.toFixed(2)}</p>
                              
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => startEditing(song)}
                                  style={{
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    flex: 1
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(song.id)}
                                  style={{
                                    backgroundColor: '#EF4444',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    flex: 1
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎵</div>
                      <p style={{ fontSize: '18px', margin: 0 }}>No songs added yet. Add your first song above!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "qrcode" && (
            <div className="qr-section">
              <div style={cardStyle}>
                <div style={{ padding: '32px', borderBottom: '4px solid #6D28D9', backgroundColor: '#581C87' }}>
                  <h2 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', margin: 0 }}>
                    <span style={{ marginRight: '16px' }}>📱</span>
                    Your QR Code
                  </h2>
                  <p style={{ color: '#C4B5FD', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>
                    Share this QR code for fans to request songs
                  </p>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '32px', textAlign: 'center' }}>
                  <div style={{ 
                    display: 'inline-block', 
                    padding: '32px', 
                    backgroundColor: 'white', 
                    borderRadius: '24px',
                    marginBottom: '32px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                  }}>
                    <QRCodeCanvas 
                      id="qr-code"
                      value={`https://requestasong-4472b.web.app/m/${user?.uid}`}
                      size={isMobile ? 200 : 300}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '32px' }}>
                    <p style={{ color: '#C4B5FD', fontSize: '18px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                      Your Page URL:
                    </p>
                    <p style={{ 
                      color: '#10B981', 
                      fontSize: isMobile ? '14px' : '16px', 
                      fontFamily: 'monospace',
                      backgroundColor: '#374151',
                      padding: '12px',
                      borderRadius: '8px',
                      margin: 0,
                      wordBreak: 'break-all'
                    }}>
                      https://requestasong-4472b.web.app/m/{user?.uid}
                    </p>
                  </div>
                  
                  <button
                    onClick={handleDownloadQRCode}
                    style={{
                      backgroundColor: '#4F46E5',
                      color: 'white',
                      fontWeight: 'bold',
                      padding: '16px 32px',
                      borderRadius: '16px',
                      border: '4px solid #4338CA',
                      fontSize: '18px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginBottom: '32px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#4338CA';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#4F46E5';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    📥 Download QR Code
                  </button>
                  
                  <div style={{
                    backgroundColor: '#1E3A8A',
                    border: '4px solid #3B82F6',
                    borderRadius: '16px',
                    padding: '24px',
                    textAlign: 'left'
                  }}>
                    <h4 style={{ color: '#BFDBFE', fontWeight: 'bold', fontSize: '18px', margin: '0 0 16px 0' }}>
                      💡 How to Use Your QR Code
                    </h4>
                    <ul style={{ color: '#BFDBFE', fontSize: '16px', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                      <li>Print and display the QR code at your performance venue</li>
                      <li>Fans scan it with their phone camera to request songs</li>
                      <li>You'll receive SMS notifications when songs are requested</li>
                      <li>Payment is authorized but only charged after you perform and they rate</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Print Ordering Section */}
              <PrintOrderInterface user={user} displayName={displayName} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}